import * as THREE from 'three';
import baseFragment from './Shader/base.frag';
import baseVertex from './Shader/base.vert';

const FONT_SIZE = 24;
const FONT_SIZE_SMALL = 12;
const LINE_HEIGHT = 1.5;
const PADDING_V = FONT_SIZE * 1.5;
const PADDING_H = FONT_SIZE * 2.5;
const PIXEL_RATIO = (() => {
  const ctx = document.createElement('canvas').getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const bsr = ctx.webkitBackingStorePixelRatio ||
    ctx.mozBackingStorePixelRatio ||
    ctx.msBackingStorePixelRatio ||
    ctx.oBackingStorePixelRatio ||
    ctx.backingStorePixelRatio || 1;

  return dpr / bsr;
})();

function getCanvas(w, h, r) {
  let ratio = r;
  if (!r) { ratio = PIXEL_RATIO; }
  const can = document.createElement('canvas');
  can.width = w * ratio;
  can.height = h * ratio;
  can.style.width = `${w}px`;
  can.style.height = `${h}px`;
  can.getContext('2d').setTransform(ratio, 0, 0, ratio, 0, 0);
  return can;
}

export default class Text3D extends THREE.Mesh {
  constructor() {
    const canvas = getCanvas(512, 1024);
    const texture = new THREE.CanvasTexture(canvas);

    super(
      new THREE.PlaneBufferGeometry(1, 1),
      new THREE.RawShaderMaterial({
        transparent: true,
        fragmentShader: baseFragment,
        vertexShader: baseVertex,
        uniforms: {
          texture: {
            value: texture,
          },
          opacity: {
            value: 0,
          },
        },
      }),
    );

    this.texture = texture;
    this.canvas = canvas;
    this.context = canvas.getContext('2d');
    this.startPosition = new THREE.Vector3();
    this.repeat = new THREE.Vector2();

    this.context.imageSmoothingEnabled = true;
  }
  update(text = '', from = '') {
    const words = text.split(' ');

    let line = '';
    let Y = FONT_SIZE_SMALL + PADDING_V;
    let X = 0;

    // Initialize
    this.context.fillStyle = '#fff';
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.fillStyle = '#000';

    // Write from first line
    this.context.font = `${FONT_SIZE_SMALL}pt/${LINE_HEIGHT} TrioGrotesk`;
    this.context.fillText(from, PADDING_H, Y);

    // Set params for corpus
    this.context.font = `${FONT_SIZE}pt/${LINE_HEIGHT} Sailec`;
    Y += FONT_SIZE * 3;

    for (let n = 0; n < words.length; n += 1) {
      const testLine = `${line} ${words[n]}`.trim();
      const metrics = this.context.measureText(testLine);
      const testWidth = metrics.width * PIXEL_RATIO;
      if (testWidth > this.canvas.width - (2 * PADDING_H * PIXEL_RATIO) && n > 0) {
        this.context.fillText(line, PADDING_H, Y);
        line = `${words[n]}`;
        Y += FONT_SIZE * LINE_HEIGHT;
      } else {
        line = testLine;

        if (X < metrics.width + (2 * PADDING_H)) {
          X = metrics.width + (2 * PADDING_H);
        }
      }
    }
    this.context.fillText(line, PADDING_H, Y);
    Y += (FONT_SIZE * LINE_HEIGHT * 0.8) - FONT_SIZE;
    Y += PADDING_V;

    // Update geometry and material
    const width = X * PIXEL_RATIO;
    const height = Y * PIXEL_RATIO;
    const ratio = height / width;

    this.repeat = new THREE.Vector2(
      width / this.canvas.width,
      height / this.canvas.height,
    );

    this.geometry.attributes.position.set([
      -1.4,
      ratio * 1.4,
      0,
      1.4,
      ratio * 1.4,
      0,
      -1.4,
      -ratio * 1.4,
      0,
      1.4,
      -ratio * 1.4,
      0,
    ]);
    this.geometry.attributes.uv.set([
      this.repeat.x,
      1,
      2 * this.repeat.x,
      1,
      this.repeat.x,
      1 - this.repeat.y,
      2 * this.repeat.x,
      1 - this.repeat.y,
    ]);

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.uv.needsUpdate = true;
    this.texture.needsUpdate = true;
  }
  setPosition(position) {
    this.position.copy(position);
    this.startPosition = position;
  }
  animate(animation) {
    const { transition, opacity, slide, expulse } = animation;
    const offset = slide * this.repeat.x;
    const t = Math.max((transition - 0.25) * 1.33333, 0);
    this.material.uniforms.opacity.value = Math.max(Math.min(opacity, 1), 0);
    this.material.uniforms.opacity.needsUpdate = true;

    this.geometry.attributes.uv.set([
      this.repeat.x - offset,
      1,
      (2 * this.repeat.x) - offset,
      1,
      this.repeat.x - offset,
      1 - this.repeat.y,
      (2 * this.repeat.x) - offset,
      1 - this.repeat.y,
    ]);
    this.geometry.attributes.uv.needsUpdate = true;
    this.scale.x = Math.min(Math.max(1 - t, 0.0000001), 1);

    this.position.x = this.startPosition.x + (1 - transition);
    this.position.y = this.startPosition.y - (expulse * 15);
  }
}



// WEBPACK FOOTER //
// ./src/scene/Text3D.js